import { Location } from '@angular/common';
import { inject, Service } from '@angular/core';
import { NavigationError } from '@angular/router';
import { LoggingService } from '../../logging/logging.service';

/**
 * Recovers from a lazy-loaded chunk that can no longer be fetched.
 *
 * This happens after a deployment: a tab that is still running the previous build asks for a hashed
 * chunk (`task-routing.module-BLHLMXU7.js`) that the new build no longer ships. A full page load picks
 * up the current `index.html` and with it the current chunk names.
 *
 * Two places report such a failure. The router calls {@link reloadOnChunkLoadError} for a failed
 * navigation, which is the only place that still knows where the user was heading: the router updates
 * the address bar only after a navigation succeeds, so at that point it still shows the previous page.
 * The router then rethrows the same error, and it reaches `GlobalErrorHandler` as well - together
 * with any failed `import()` outside the router, for which reloading the current page is right.
 */
@Service()
export class ChunkLoadRecoveryService {
    private static readonly CHUNK_LOAD_ERROR_MESSAGES = [
        /Failed to fetch dynamically imported module/, // Chromium
        /error loading dynamically imported module/, // Firefox
        /Importing a module script failed/, // Safari
        /Loading chunk [\d]+ failed/, // webpack, kept for older builds
    ];
    private static readonly RELOAD_STORAGE_KEY = 'curafida.chunkLoadErrorReloadAt';
    private static readonly RELOAD_COOLDOWN_MS = 10_000;
    protected readonly log = inject(LoggingService).getLogger(this.constructor.name);
    /** Errors already recovered from, so that the rethrow reaching `GlobalErrorHandler` does not act twice. */
    private readonly handledErrors = new WeakSet<object>();

    /**
     * Tells whether a lazy-loaded chunk could not be fetched.
     *
     * The esbuild builder loads chunks with a native `import()`, so the message is the browser's own and
     * differs per engine. A promise rejection reaches the error handler wrapped by Zone, hence the look
     * into `rejection`.
     */
    isChunkLoadError(error: any): boolean {
        const message = this.unwrap(error)?.message;
        if (typeof message !== 'string') return false;
        return ChunkLoadRecoveryService.CHUNK_LOAD_ERROR_MESSAGES.some((pattern) => pattern.test(message));
    }

    /**
     * Loads `targetUrl` from the server, or reloads the current page when there is no target.
     *
     * A chunk that is missing for any other reason - the server is down, the device is offline - would
     * still be missing after the reload, so a second failure within the cooldown is only logged instead
     * of reloading the page in an endless loop. An error that was already recovered from is ignored.
     */
    recover(error: any, targetUrl?: string): void {
        const unwrapped = this.unwrap(error);
        if (this.isObject(unwrapped)) {
            if (this.handledErrors.has(unwrapped)) return;
            this.handledErrors.add(unwrapped);
        }

        const now = Date.now();
        const lastReloadAt = Number(sessionStorage.getItem(ChunkLoadRecoveryService.RELOAD_STORAGE_KEY));
        if (lastReloadAt && now - lastReloadAt < ChunkLoadRecoveryService.RELOAD_COOLDOWN_MS) {
            this.log.error('[recover] chunk still missing after a reload, giving up', error);
            return;
        }
        this.log.warn(`[recover] chunk missing, probably a new deployment -> loading ${targetUrl ?? 'current page'}`);
        sessionStorage.setItem(ChunkLoadRecoveryService.RELOAD_STORAGE_KEY, String(now));
        if (targetUrl) {
            this.navigateTo(targetUrl);
        } else {
            this.reloadPage();
        }
    }

    /** Own method so that specs can replace it: jsdom implements no navigation. */
    protected navigateTo(url: string): void {
        window.location.assign(url);
    }

    /** Own method so that specs can replace it: jsdom implements no navigation. */
    protected reloadPage(): void {
        window.location.reload();
    }

    private unwrap(error: any): any {
        return error?.rejection ?? error;
    }

    private isObject(value: unknown): value is object {
        return (typeof value === 'object' && value !== null) || typeof value === 'function';
    }
}

/**
 * Navigation error handler for `withNavigationErrorHandler`: on a missing chunk, loads the page the
 * user was navigating to rather than reloading the one they are leaving.
 *
 * Returns nothing, so the router still emits the `NavigationError` and rejects the navigation as usual.
 */
export function reloadOnChunkLoadError(navigationError: NavigationError): void {
    const recovery = inject(ChunkLoadRecoveryService);
    if (!recovery.isChunkLoadError(navigationError.error)) return;
    recovery.recover(navigationError.error, inject(Location).prepareExternalUrl(navigationError.url));
}
